import React from 'react';
import { IdentityProviderManager } from './IdentityProviderManager/IdentityProviderManager';
import { AuditLog } from './AuditLog/AuditLog';
import { UasMigration } from '../UasMigration/UasMigration';
import { AccessControlledSection } from '../../RequiresApi/AccessControlledSection';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { useFeatureContext } from '../../../contexts/FeatureContext';

/**
 * Content of Admin > Settings > Authentication.
 *
 * Visibility is gated by the `developer/settings/authentication` access entry:
 * the auth-prov API available, an admin or support user, and the tenant-scoped
 * `bbdata.auth.management` flag.
 *
 * The UAS migration entry point is the existing `UasMigration` component, the
 * same one Snowflake Account Settings renders, rather than a second banner of
 * our own. It brings its own access gate and feature flag, so turning UAS
 * migration off hides it from both tabs alike.
 */
export const AuthenticationTab: React.FunctionComponent = () => {
    const access = useAccessControl();
    const showUasMigration = useFeatureContext().uasMigrationEnabled;

    /**
     * Adding, editing and deleting all write an audit entry, so the log below
     * is stale the moment one of the actions above happens. The manager reports the change rather than
     * the log polling for it.
     */
    const [auditToken, setAuditToken] = React.useState(0);
    const onMutated = React.useCallback(() => setAuditToken(n => n + 1), []);

    return (
        <>
            <IdentityProviderManager onMutated={onMutated} />
            {showUasMigration && (
                <AccessControlledSection
                    access={access['developer/settings/uas-migration']}
                    onApiMissing="HIDE"
                >
                    <UasMigration />
                </AccessControlledSection>
            )}
            <AuditLog reloadToken={auditToken} />
        </>
    );
};
